import type { Fact } from '../engine/facts';
import type { MasteryState } from '../engine/mastery';
import type { FactStats } from '../engine/stats';
import { dateTime, expr, percent, seconds } from './format';
import { Hint, Screen, Title } from './kit';
import { MASTERY_COLOR, MASTERY_LABEL, MASTERY_NOTE } from './mastery-view';

interface Props {
  fact: Fact;
  state: MasteryState;
  stats: FactStats;
  onBack: () => void;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2 border-b border-line py-2 last:border-0">
      <span className="text-[0.9rem] text-muted">{label}</span>
      <span className="ml-auto font-semibold tabular-nums">{value}</span>
    </div>
  );
}

export function FactScreen({ fact, state, stats, onBack }: Props) {
  return (
    <Screen>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          aria-label="Назад"
          className="size-11 flex-none rounded-full bg-card text-[1.1rem] text-muted"
        >
          ←
        </button>
        <Title>
          {expr(fact)} = {fact.answer}
        </Title>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-line bg-card p-3">
        <span
          aria-hidden="true"
          className="size-3 flex-none rounded-full"
          style={{ background: MASTERY_COLOR[state] }}
        />
        <span className="font-semibold">{MASTERY_LABEL[state]}</span>
        <span className="ml-auto text-right text-[0.85rem] text-muted">
          {MASTERY_NOTE[state]}
        </span>
      </div>

      {/* «Не встречался» — только когда факта нет в журнале ВОВСЕ. Раздел
          6.5 считает сравнение предъявлением, и движок с этим согласен:
          факт с одними сравнениями получает «Данных мало», а не «Не
          встречался». Пока условие смотрело на одни зачётные появления,
          экран показывал три взаимоисключающих утверждения разом —
          «Данных мало», «ещё не встречался» и «В сравнениях: 3». */}
      {stats.appearances === 0 && stats.comparisons.appearances === 0 ? (
        <Hint>Он появится в тренировке, когда выберешь таблицу на {fact.tile}.</Hint>
      ) : stats.appearances === 0 ? (
        <Hint>Этот пример встречался только в заданиях на сравнение</Hint>
      ) : (
        <div className="rounded-xl border border-line bg-card px-3">
          <Row label="Появлений" value={String(stats.appearances)} />
          <Row label="Верно" value={String(stats.correct)} />
          <Row label="Ошибок" value={String(stats.wrong)} />
          <Row label="Точность" value={percent(stats.accuracy)} />
          <Row label="Среднее время" value={seconds(stats.avgAllMs)} />
          <Row label="Среднее время верных" value={seconds(stats.avgCorrectMs)} />
          <Row label="Лучшее время" value={seconds(stats.bestCorrectMs)} />
          <Row
            label="Последняя попытка"
            value={stats.lastAtMs === null ? '—' : dateTime(stats.lastAtMs)}
          />
        </div>
      )}

      {stats.recent.length > 0 && (
        <div className="grid gap-1.5">
          <Hint>Последние попытки</Hint>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
            {stats.recent.map((item, index) => (
              <li
                key={index}
                className={`rounded-lg border px-2 py-1 text-sm tabular-nums ${
                  item.isCorrect
                    ? 'border-correct bg-correct-soft text-correct'
                    : 'border-incorrect bg-incorrect-soft text-incorrect'
                }`}
              >
                {item.isCorrect ? seconds(item.answerMs) : '✕'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Сравнения отдельной строкой и не в общих счётчиках: они слабое
          свидетельство о конкретном факте (раздел 6.5). */}
      <Hint>
        {stats.comparisons.appearances === 0
          ? 'В заданиях на сравнение не встречался'
          : `В сравнениях: ${stats.comparisons.appearances}, из них верно ${stats.comparisons.correct}`}
      </Hint>
    </Screen>
  );
}
